import { ENTITY_BASED_DISABLED_COLOR, ENTITY_BASED_ENABLED_COLOR, GRAPH_COLORS } from "components/Graph/constants";
import {
    HighlightsLocalStorageLeaf,
    useHighlightsLocalStorage,
} from "components/Graph/hooks/useHighlightsLocalStorage";
import { GRAPH_TYPE, GraphId, GraphType, Highlight, HighlightState } from "components/Graph/types";
import { useState } from "react";

interface Args<TData extends object, CPath extends keyof TData> {
    data: TData[];
    keys: CPath[];
    graphId: GraphId;
    defaultActiveKeys: CPath[];
    defaultVisibleKeys: CPath[];
    onActiveHighlightChange?: (key: CPath) => void;
}

export const useHighlights = <TData extends object, CPath extends keyof TData>({
    keys,
    graphId,
    defaultActiveKeys,
    defaultVisibleKeys,
    onActiveHighlightChange,
}: Args<TData, CPath>) => {
    const { get: getLC, set: setLC } = useHighlightsLocalStorage();
    const [colors, setColors] = useState(Object.values(GRAPH_COLORS));

    const localStorage = getLC(graphId);
    const graphType = GRAPH_TYPE[graphId];

    const activeHighlightsFromStorage = getActiveHighlightsFromStorage<CPath>(localStorage, graphType);
    const [activeHighlights, setActiveHighlights] = useState<CPath[]>(
        activeHighlightsFromStorage.length > 0 ? activeHighlightsFromStorage : defaultActiveKeys
    );

    const [highlights, setHighlights] = useState<Array<Highlight<(typeof keys)[number]>>>(() => {
        const { assignedColors: defaultAssignedColors, highlights: defaultHighlights } =
            getDefaultHighlightsFromLocalStorage(localStorage, keys);

        if (defaultHighlights.length > 0) {
            if (graphType === "time") {
                setColors(Object.values(GRAPH_COLORS).filter((color) => !defaultAssignedColors.includes(color)));
            }

            return defaultHighlights;
        }

        const { assignedColors, defaultVisibleKeys: constructedDefaults } = constructHighlightsFromKeys(
            defaultVisibleKeys,
            defaultActiveKeys,
            colors,
            graphType
        );

        setLC(constructedDefaults, graphId);
        setColors(Object.values(GRAPH_COLORS).filter((color) => !assignedColors.includes(color)));

        return constructedDefaults;
    });

    const handleClick = (key: CPath) => {
        let _highlights = [...highlights];

        if (graphType === "entity") {
            const index = _highlights.findIndex((h) => h.key === key);

            if (_highlights[index]?.state === "active") {
                return;
            }

            _highlights = _highlights.map((item, _index) => ({
                ...item,
                state: index === _index ? "active" : "disabled",
                color: index === _index ? ENTITY_BASED_ENABLED_COLOR : ENTITY_BASED_DISABLED_COLOR,
            }));

            setActiveHighlights([key]);
            onActiveHighlightChange?.(key);
        }

        if (graphType === "time") {
            const index = _highlights.findIndex((h) => h.key === key);
            const highlight = _highlights[index];

            _highlights[index] = { ...highlight, state: highlight.state === "active" ? "disabled" : "active" };
            setActiveHighlights(_highlights.filter((h) => h.state === "active").map((h) => h.key));
        }

        setLC(_highlights, graphId);
        setHighlights(_highlights);
    };

    const handleHide = (key: CPath) => {
        const values = highlights.filter((h) => h.key !== key);

        if (graphType === "time") {
            const removed = highlights.find((h) => h.key === key);

            if (removed?.color) {
                setColors((prev) => [...prev, removed.color]);
            }

            if (removed?.state === "active") {
                setActiveHighlights((prev) => prev.filter((k) => k !== key));
            }
        }

        setLC(values, graphId);
        setHighlights(values);
    };

    const handleAdd = (key: CPath) => {
        if (highlights.some((h) => h.key === key)) {
            return;
        }

        if (graphType === "entity") {
            const newHighlight: Highlight<(typeof keys)[number]> = {
                key,
                state: "disabled",
                color: ENTITY_BASED_DISABLED_COLOR,
            };

            const updatedHighlights = [newHighlight, ...highlights];

            setLC(updatedHighlights, graphId);
            setHighlights(updatedHighlights);
        }

        if (graphType === "time") {
            const availableColors = [...colors];

            const color = availableColors.pop();

            if (!color) {
                return;
            }

            const newHighlight: Highlight<(typeof keys)[number]> = {
                key,
                color,
                state: "disabled",
            };

            const updatedHighlights = [newHighlight, ...highlights];

            setLC(updatedHighlights, graphId);
            setColors(availableColors);
            setHighlights(updatedHighlights);
        }
    };

    const hiddenHighlights = keys.filter((key) => !highlights.some((h) => h.key === key));
    const mergedHighlights: Highlight<CPath>[] = [
        ...highlights,
        ...hiddenHighlights.map((key) => ({ key, color: "", state: "disabled" as HighlightState, hidden: true })),
    ];

    const colorKeyMap = new Map<string, string>(Object.entries(localStorage).map(([k, v]) => [k, v.color]));

    return {
        handleAdd,
        handleHide,
        handleClick,
        colorKeyMap,
        activeHighlights,
        highlights: mergedHighlights,
    };
};

const getActiveHighlightsFromStorage = <T extends string | number | symbol>(
    storageLeaf: HighlightsLocalStorageLeaf,
    graphType: GraphType
): T[] => {
    if (graphType === "entity") {
        const item = Object.entries(storageLeaf).find(([, h]) => h.state === "active")?.[0] as T;
        return item ? [item] : [];
    }

    return Object.entries(storageLeaf)
        .filter(([, h]) => h.state === "active")
        .map(([k]) => k as T);
};

const getDefaultHighlightsFromLocalStorage = <T extends string | number | symbol>(
    storageLeaf: HighlightsLocalStorageLeaf,
    keys: T[]
) => {
    const assignedColors: string[] = [];

    return {
        assignedColors,
        highlights: Object.entries(storageLeaf)
            .map(([_key, { state, color }]) => {
                const key = _key as T;

                if (!keys.includes(key)) {
                    return null;
                }

                assignedColors.push(color);

                return {
                    key,
                    state,
                    color,
                };
            })
            .filter((item): item is Highlight<(typeof keys)[number]> => Boolean(item)),
    };
};

const constructHighlightsFromKeys = <T extends string | number | symbol>(
    defaultVisibleKeys: T[],
    defaultActiveKeys: T[],
    colors: string[],
    graphType: GraphType
) => {
    const assignedColors: string[] = [];

    return {
        assignedColors,
        defaultVisibleKeys: defaultVisibleKeys.map((key, index) => {
            let color = "";
            let state = "active";

            if (graphType === "entity" && !defaultActiveKeys.includes(key)) {
                state = "disabled";
            }

            if (graphType === "time") {
                color = colors[index % colors.length];
                assignedColors.push(color);
            } else {
                color = state === "active" ? ENTITY_BASED_ENABLED_COLOR : ENTITY_BASED_DISABLED_COLOR;
            }

            return {
                key,
                color,
                state,
            } as Highlight<T>;
        }),
    };
};
